import React, { useEffect, useState } from "react";
import { Loader2, History, ChevronLeft, ChevronRight } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDateTime } from "@/lib/format";

const ACTION_LABELS = {
  "booking.status_change": "Status changed",
  "booking.bulk_status_change": "Bulk status change",
  "booking.duplicate": "Booking duplicated",
  "booking.refund": "Booking refunded",
  "review.update": "Review updated",
  "review.delete": "Review deleted",
  "settings.update": "Settings updated",
};

export default function AdminAuditLog() {
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/audit-log?page=${page}&page_size=50`);
        const d = unwrap(r);
        setItems(d.items || []);
        setTotal(d.total || 0);
      } finally { setLoading(false); }
    })();
  }, [page]);

  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-5" data-testid="admin-audit-log">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">Audit log</h1>
        <p className="text-[#4B5563] text-sm mt-1">{total} events · who did what when</p>
      </div>

      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#4B5563]">
            <History className="w-7 h-7 mx-auto mb-2 text-[#9CA3AF]" />
            No audit entries yet.
          </div>
        ) : (
          <ul className="divide-y divide-[#E5E7EB]">
            {items.map((e) => (
              <li key={e.id} className="px-5 py-3 flex items-start gap-4 hover:bg-[#FAFAF9]" data-testid={`audit-entry-${e.id}`}>
                <div className="text-xs text-[#9CA3AF] font-mono w-32 shrink-0 pt-0.5">{formatDateTime(e.created_at, locale)}</div>
                <Badge className="bg-[#0EA5A4]/10 text-[#0F766E] border-[#0EA5A4]/20 font-medium">
                  {ACTION_LABELS[e.action] || e.action}
                </Badge>
                <div className="text-sm flex-1">
                  <span className="text-[#0C1A1A] font-semibold">{e.actor_email || "—"}</span>
                  {e.target_id && <span className="text-[#9CA3AF]"> · <span className="font-mono">{e.target_id.slice(-8)}</span></span>}
                  {e.details && Object.keys(e.details).length > 0 && (
                    <span className="text-[#4B5563] text-xs ml-2">
                      {Object.entries(e.details).map(([k, v]) => (
                        <span key={k} className="font-mono mr-2">{k}={String(v)}</span>
                      ))}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="px-5 py-3 border-t border-[#E5E7EB] flex items-center justify-between text-sm">
          <span className="text-[#4B5563]">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-xl" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
